/* eslint-disable no-unused-vars */
import { useRef, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Pause, Start, ArchiveBig, Pen, ErrorMarker } from 'assets/icons';
import { redirectToOldPlatformUrl } from 'utils';
import { CopyString, CustomTooltip, IconButton, ParagraphWithTooltip } from 'components';
import * as S from './CampaignName.styled';
import { useSignal } from '@preact/signals-react';
import { ChangeCampaignNameTooltip } from './ChangeCampaignNameTooltip';
import { TooltipMessage } from './TooltipMessage';
import { useSelector } from 'react-redux';
import { isAdminSelector } from 'store/selectors';
import { isDateWasReached, getDynamicUrl, choseCampaignStatusIcon } from 'utils';
import { routes } from 'config';

const choseIconStatus = {
  3: Pause,
  0: Start,
  9: ArchiveBig,
};

export const CampaignName = (props) => {
  const {
    campaign,
    _campaignid,
    campaignId,
    campaignType,
    campaignTypeId,
    countries,
    isAllPlacements,
    _app,
    app,
    target_type,
    campaignGoalId,
    is_weak,
    isOnBoarding,
    isCustomAudience,
    customAudienceIsInclude,
    campaignGoalName,
    schedulerStartDate,
    schedulerEndDate,
    banner_list_no_tracking,
    pba_number,
    status,
    bid_type,
    campaignStatus,
    payment_status,
    _hasBanners,
    _hasTracking,
    statusDescr,
    status_name,
    call_to_action,
    packageName,
    biddingCountries,
  } = props.data;

  const rowIndex = props.rowIndex;
  const isAdmin = useSelector(isAdminSelector);

  const isCpm =
    bid_type === 3 &&
    !isOnBoarding &&
    campaignType === 'User acquisition' &&
    isAdmin &&
    countries !== '' &&
    !isAllPlacements &&
    biddingCountries &&
    biddingCountries?.length > 0;

  const title = campaign + (isCpm ? ' – CPM' : '');

  const btnRef = useRef();
  const isOpenTooltip$ = useSignal(false);

  const handleClickOutside = (event) => {
    if (btnRef.current && btnRef.current.contains(event.target)) return;
    if (isOpenTooltip$.value && btnRef.current && !btnRef.current.contains(event.target)) {
      isOpenTooltip$.value = false;
    }
  };

  const IconStatus2 = choseCampaignStatusIcon({
    campaignStatus,
    payment_status,
    _hasBanners,
    bid_type,
    schedulerStartDate,
    schedulerEndDate,
    startWasReached: isDateWasReached(schedulerStartDate),
    endWasReached: isDateWasReached(schedulerEndDate),
    banner_list_no_tracking,
    statusDescr,
    status_name,
    call_to_action,
    status,
  });
  const isLimited = statusDescr?.includes('limit') && status_name === 'open';
  const tooltipStatusIcon =
    status == 4 || call_to_action == 4 ? (
      <IconStatus2 />
    ) : isLimited && campaignStatus == 0 ? (
      <ErrorMarker />
    ) : (
      <IconStatus2 />
    );

  let lastScrollY = window.scrollY;
  let tooltipManuallyClosed = false;

  const handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (Math.abs(currentScrollY - lastScrollY) > 2 && !tooltipManuallyClosed) {
      isOpenTooltip$.value = false;
      window.removeEventListener('scroll', handleScroll, true);
    }

    lastScrollY = currentScrollY;
  };

  const toggleTooltip = () => {
    isOpenTooltip$.value = true;
    tooltipManuallyClosed = false;
    lastScrollY = window.scrollY;

    window.addEventListener('scroll', handleScroll, {
      capture: true,
    });

    document.addEventListener('click', handleClickOutside, {
      capture: true,
      once: true,
    });
  };

  const isDeletedCampaign = campaignStatus === 9;

  const navigate = useNavigate();

  // const handleRedirectionToReport = useCallback(() => {
  //   if (window.REACT_APP_ENV === 'qa') {
  //     let url = getDynamicUrl(routes.list.find((route) => route.id === 'campaignReports').url, {
  //       cmp_id: campaignId,
  //     });
  //     navigate(url);
  //     //navigate(`/Discovery/CampaignReports?id=${campaignId}`);
  //   } else if (window.REACT_APP_ENV === 'production') {
  //     const newUrl = redirectToOldPlatformUrl(`/Discovery/Reports.aspx?id=${campaignId}`);
  //     window.location.href = newUrl;
  //   }
  // }, [campaignId]);

  const getRedirectionURLToReport = useCallback(() => {
    if (window.REACT_APP_ENV === 'qa') {
      let url = getDynamicUrl(routes.list.find((route) => route.id === 'campaignReports').url, {
        cmp_id: campaignId,
      });
      return url;
    } else if (window.REACT_APP_ENV === 'production') {
      const newUrl = redirectToOldPlatformUrl(`/Discovery/Reports.aspx?id=${campaignId}`);
      return newUrl;
    }
  }, [campaignId]);

  const isDraft = useMemo(() => {
    if (campaignStatus === 3) {
      return _hasBanners === 0 || banner_list_no_tracking !== '';
    }
    return (
      (campaignStatus === 3 &&
        bid_type === 3 &&
        _hasBanners === 0 &&
        banner_list_no_tracking !== '') ||
      (campaignStatus === 3 && bid_type === 2 && _hasBanners === 0)
    );
  }, [bid_type, _hasBanners, banner_list_no_tracking, campaignStatus]);

  const iconSize = useMemo(() => {
    return isDeletedCampaign ||
      isDraft ||
      (campaignStatus == 0 && !schedulerEndDate) ||
      (campaignStatus == 0 && !isDateWasReached(schedulerEndDate)) ||
      (campaignStatus == 3 && !schedulerStartDate) ||
      (campaignStatus == 3 && !isDateWasReached(schedulerStartDate)) ||
      campaignStatus == 4 ||
      campaignStatus == 8
      ? '20px'
      : '24px';
  }, [isDeletedCampaign, isDraft, schedulerEndDate, schedulerStartDate, campaignStatus]);

  return (
    <S.Container>
      <S.IconBox>
        <CustomTooltip
          text={
            <TooltipMessage
              statusDescr={statusDescr}
              status_name={status_name}
              call_to_action={call_to_action}
              campaignStatus={campaignStatus}
              status={status}
              isDraft={isDraft}
              _hasBanners={_hasBanners}
              banner_list_no_tracking={banner_list_no_tracking}
              icon={tooltipStatusIcon}
              schedulerStartDate={schedulerStartDate}
              schedulerEndDate={schedulerEndDate}
            />
          }
          content={
            <IconStatus2
              style={{
                minWidth: iconSize,
                minHeight: iconSize,
                cursor: isDeletedCampaign && 'auto',
              }}
            />
          }
        />
      </S.IconBox>
      <S.TextBox>
        <S.NameContainer>
          <S.Text
            isDeleted={isDeletedCampaign}
            //onClick={handleRedirectionToReport}
          >
            <Link
              to={getRedirectionURLToReport()}
              style={{ textDecoration: 'none', color: 'inherit', cursor: 'inherit' }}
            >
              <ParagraphWithTooltip text={title} />
            </Link>
          </S.Text>
          {!isDeletedCampaign && (
            <CustomTooltip
              style={{ marginLeft: '4px' }}
              text={
                <ChangeCampaignNameTooltip
                  campaignName={campaign}
                  ref={btnRef}
                  isOpen$={isOpenTooltip$}
                  campaignId={campaignId}
                />
              }
              content={
                <IconButton.Micro
                  icon={Pen}
                  type={IconButton.btnTypes.tertiary}
                  isPressed={isOpenTooltip$.value}
                  onClick={toggleTooltip}
                />
              }
              isOpen={isOpenTooltip$.value}
            />
          )}
        </S.NameContainer>
        <S.AppIdWrap>
          {props.data?.isWebsite ? 'Website' : 'App ID'}: <S.AppId>{packageName}</S.AppId>
        </S.AppIdWrap>
        <CopyString label='ID' text={_campaignid?.toString()} />
      </S.TextBox>
    </S.Container>
  );
};
